import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';

const STATUS_LABEL: Record<string, string> = { active: 'Ativo', suspended: 'Suspenso' };
const STATUS_CLS: Record<string, string> = { active: 'bg-secondary/15 text-secondary', suspended: 'bg-error-container/25 text-error' };
const RISK_LABEL: Record<string, string> = { normal: 'Normal', watch: 'Observação', high: 'Alto' };
const RISK_CLS: Record<string, string> = {
  normal: 'bg-surface-container-highest text-outline',
  watch: 'bg-tertiary/15 text-tertiary',
  high: 'bg-error-container/25 text-error',
};
const STORE_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  unpublished: 'Despublicada',
  suspended: 'Suspensa',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

const SIZE = 20;

/** Admin · Lojas & Subdomínios (era "Parceiros") — lista com busca + drill-down em
 *  `/parceiros/:id`. Consome `GET /api/admin/partners`. Redesign Lumina: o protótipo Super
 *  Admin tem coluna "Plano" (Enterprise/Scale/Starter) — a Storeo não tem tiers, é comissão
 *  flat 7,99% pra todo mundo — trocado por Risco, que é dado real. */
export function Parceiros() {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const partners = useQuery({
    queryKey: ['admin', 'partners', term, page],
    queryFn: () => api.partners({ search: term || undefined, page, size: SIZE }),
  });

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-primary-container/10 blur-[100px] pointer-events-none" />
        <div className="relative z-10 flex flex-col xl:flex-row xl:items-end justify-between gap-space-lg">
          <div className="flex flex-col max-w-3xl gap-space-2xs">
            <h1 className="font-headline-lg text-headline-lg font-bold text-on-surface tracking-tight">Lojas & Subdomínios</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">Todo parceiro cadastrado na plataforma, com a loja e o GMV de cada um.</p>
          </div>
        </div>
      </div>

      <form
        className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-space-md bg-surface-container-low p-space-md rounded-xl shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
          setTerm(search.trim());
        }}
      >
        <div className="relative flex-1 max-w-lg">
          <MSym name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-outline text-[18px]" />
          <input
            className="w-full pl-10 pr-4 py-2 text-body-sm font-body-sm bg-surface-container-lowest text-on-surface placeholder:text-outline rounded-lg outline-none focus:bg-surface-container transition-all"
            placeholder="Buscar por nome, e-mail ou subdomínio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="px-space-md py-2 rounded-lg bg-surface-container-high hover:bg-surface-bright text-on-surface font-label-sm text-label-sm transition-colors" type="submit">
          Buscar
        </button>
      </form>

      <div className="flex flex-col bg-surface-container-low rounded-2xl p-space-md shadow-lg overflow-hidden">
        {partners.isLoading ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant p-space-md">Carregando…</p>
        ) : !partners.data?.length ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant p-space-md">
            {page > 0 ? 'Não há mais parceiros.' : 'Nenhum parceiro encontrado.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-outline font-label-sm text-label-sm uppercase tracking-wider bg-surface-container-lowest/60">
                  <th className="py-space-sm px-space-md rounded-l-lg">Parceiro</th>
                  <th className="py-space-sm px-space-sm">Loja</th>
                  <th className="py-space-sm px-space-sm">Status</th>
                  <th className="py-space-sm px-space-sm">Risco</th>
                  <th className="py-space-sm px-space-md text-right rounded-r-lg">GMV</th>
                </tr>
              </thead>
              <tbody className="text-body-sm font-body-sm">
                {partners.data.map((p) => (
                  <tr key={p.id} className="group hover:bg-surface-container/60 transition-colors">
                    <td className="py-space-md px-space-md">
                      <Link to={`/parceiros/${p.id}`} className="flex items-center gap-space-sm">
                        <div className="w-10 h-10 rounded-xl bg-primary-container/20 flex items-center justify-center text-primary font-bold text-headline-sm shrink-0">
                          {p.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="font-label-lg text-label-lg font-bold text-on-surface group-hover:text-primary transition-colors truncate">{p.name}</span>
                          <span className="text-outline font-label-sm text-label-sm truncate">{p.email}</span>
                        </div>
                      </Link>
                    </td>
                    <td className="py-space-md px-space-sm">
                      {p.storeId ? (
                        <div className="flex flex-col">
                          <span className="text-on-surface font-semibold">{p.storeName}</span>
                          <span className="text-outline font-label-sm text-label-sm">
                            {p.subdomain} · {STORE_STATUS_LABEL[p.storeStatus] ?? p.storeStatus}
                          </span>
                        </div>
                      ) : (
                        <span className="text-outline">—</span>
                      )}
                    </td>
                    <td className="py-space-md px-space-sm">
                      <span className={`px-space-xs py-1 rounded-full font-label-sm text-label-sm font-bold inline-block ${STATUS_CLS[p.status] ?? 'bg-surface-container-highest text-outline'}`}>
                        {STATUS_LABEL[p.status] ?? p.status}
                      </span>
                    </td>
                    <td className="py-space-md px-space-sm">
                      <span className={`px-space-xs py-1 rounded-full font-label-sm text-label-sm font-bold inline-block ${RISK_CLS[p.risk] ?? 'bg-surface-container-highest text-outline'}`}>
                        {RISK_LABEL[p.risk] ?? p.risk}
                      </span>
                    </td>
                    <td className="py-space-md px-space-md text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-bold text-on-surface font-label-lg text-label-lg">{money(p.gmv)}</span>
                        <span className="text-outline font-label-sm text-label-sm">desde {when(p.createdAt)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center gap-space-xs self-end">
        <button
          className="px-space-md py-1.5 rounded-lg bg-surface-container-low hover:bg-surface-container-high text-on-surface font-label-sm text-label-sm transition-colors disabled:opacity-40"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Mais recentes
        </button>
        <button
          className="px-space-md py-1.5 rounded-lg bg-surface-container-low hover:bg-surface-container-high text-on-surface font-label-sm text-label-sm transition-colors disabled:opacity-40"
          disabled={(partners.data?.length ?? 0) < SIZE}
          onClick={() => setPage((p) => p + 1)}
        >
          Mais antigos →
        </button>
      </div>
    </>
  );
}
